// =============================================================================
// AddressCaptureModal.jsx — structured one-off delivery-address entry
// =============================================================================
// The shelter's "Different address" flow on a New Request. Captures the address
// as STRUCTURED components (so it lands correctly on the Unleashed Sales Order —
// DeliveryCity / Region / Postcode — instead of a single unstructured blob) plus
// a required name. Google autocomplete FILLS the component fields; the user can also
// type/correct any field directly.
//
// The name prints on the delivery (Unleashed Delivery Name), so since 25 Oct 2026 it
// STARTS EMPTY (no "Delivery N" prefill) with the rule explained under it — open
// address = organisation name; confidential = "Att:" + contact. See addressName.ts.
//
// Only used by ShelterDeliverySelect (New Request general delivery). The responder
// EditAddressModal + the per-child device DeliverySelect are intentionally left on
// the older single-line picker, so this modal is never nested inside another modal.
//
// Props:
//   initial       - {name, street, street2, suburb_city, state, postcode} to edit an
//                   already-entered one-off; omit for a fresh entry.
//   onSave(addr)  - called with {name, street, street2, suburb_city, state, postcode}.
//   onClose()     - dismiss without saving.
// =============================================================================

import { useId, useState } from "react";
import {
  ADDRESS_NAME_PLACEHOLDER,
  ADDRESS_NAME_REQUIRED_MSG,
  isBlankAddressName,
} from "../../services/addressName";
import { AddressAutocomplete } from "./AddressAutocomplete.jsx";
import { AddressNameHelp } from "./AddressNameHelp.jsx";
import { ModalBackdrop } from "./FormUI.jsx";

const NAME_HELP_ID = "capture-addr-name-help";

function Field({ label, value, onChange, placeholder, error, autoComplete, helpId, help }) {
  const id = useId();
  return (
    <div style={{ marginBottom: 12 }}>
      <label
        htmlFor={id}
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 600,
          color: "#607D79",
          marginBottom: 4,
        }}
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value || ""}
        autoComplete={autoComplete || "off"}
        onChange={function (e) {
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        aria-describedby={helpId}
        style={{
          width: "100%",
          padding: "9px 12px",
          borderRadius: 8,
          border: error ? "1.5px solid #EF5350" : "1.5px solid #CFE0DE",
          fontSize: 13,
          color: "#2C3E3B",
          boxSizing: "border-box",
          fontFamily: "inherit",
        }}
      />
      {error && <div style={{ marginTop: 3, fontSize: 12, color: "#C62828" }}>{error}</div>}
      {help}
    </div>
  );
}

export function AddressCaptureModal({ initial, onSave, onClose }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [street, setStreet] = useState(initial?.street || "");
  const [street2, setStreet2] = useState(initial?.street2 || "");
  const [suburb, setSuburb] = useState(initial?.suburb_city || "");
  const [state, setState] = useState(initial?.state || "");
  const [postcode, setPostcode] = useState(initial?.postcode || "");
  const [showErrors, setShowErrors] = useState(false);

  // Required for clean Sales Order components: a name + the structured parts. street2
  // is optional. Postcode must be a 4-digit AU postcode.
  const errors = {
    name: isBlankAddressName(name) ? ADDRESS_NAME_REQUIRED_MSG : "",
    street: !street.trim() ? "Street address is required." : "",
    suburb: !suburb.trim() ? "Suburb / City is required." : "",
    state: !state.trim() ? "State is required." : "",
    postcode: !/^\d{4}$/.test(postcode.trim()) ? "A 4-digit postcode is required." : "",
  };
  const hasError = Object.values(errors).some(Boolean);

  function handleSave() {
    if (hasError) {
      setShowErrors(true);
      return;
    }
    onSave({
      name: name.trim(),
      street: street.trim(),
      street2: street2.trim(),
      suburb_city: suburb.trim(),
      state: state.trim(),
      postcode: postcode.trim(),
    });
  }

  return (
    <ModalBackdrop
      onClose={onClose}
      contentStyle={{
        background: "white",
        borderRadius: 12,
        width: "calc(100vw - 40px)",
        maxWidth: 520,
        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        // Keep visible so the Google Places dropdown isn't clipped (see CLAUDE.md).
        overflow: "visible",
        fontFamily: "inherit",
      }}
    >
      <div className="bg-zephyr-teal-dark text-white py-[14px] px-5 rounded-t-[12px] flex justify-between items-center">
        <span className="font-bold text-base">
          {isEdit ? "Edit delivery address" : "Add a different delivery address"}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="bg-white/20 border border-white/40 rounded-[6px] text-white text-base font-bold cursor-pointer leading-none py-1 px-[10px]"
        >
          X
        </button>
      </div>
      <div className="p-6">
        <div
          style={{
            fontSize: 12,
            color: "#7c4a00",
            background: "#FFF3E0",
            border: "1px solid #FFB300",
            borderRadius: 6,
            padding: "8px 12px",
            marginBottom: 14,
          }}
        >
          <strong>THIS MUST NOT BE THE CLIENT&rsquo;S ADDRESS.</strong> Start typing the street to
          pick it from the list — the fields below fill in automatically. A Responder will verify it
          before dispatching.
        </div>

        <Field
          label="Address name"
          value={name}
          onChange={setName}
          placeholder={ADDRESS_NAME_PLACEHOLDER}
          error={showErrors ? errors.name : ""}
          helpId={NAME_HELP_ID}
          help={<AddressNameHelp id={NAME_HELP_ID} />}
        />

        {/* Autocomplete on the street field fills the structured fields below.
            A plain caption (not <label>) — the control is the autocomplete input. */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#607D79", marginBottom: 4 }}>
            Street address
          </div>
          <AddressAutocomplete
            value={street}
            onChange={setStreet}
            onSelect={function (parts) {
              setStreet(parts.street || "");
              setStreet2(parts.street2 || "");
              setSuburb(parts.suburb || "");
              setState(parts.state || "");
              setPostcode(parts.postcode || "");
            }}
            placeholder="Start typing the street address..."
            inputStyle={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: 8,
              border: showErrors && errors.street ? "1.5px solid #EF5350" : "1.5px solid #CFE0DE",
              fontSize: 13,
              color: "#2C3E3B",
              boxSizing: "border-box",
            }}
          />
          {showErrors && errors.street && (
            <div style={{ marginTop: 3, fontSize: 12, color: "#C62828" }}>{errors.street}</div>
          )}
        </div>

        <Field
          label="Street address line 2 (optional)"
          value={street2}
          onChange={setStreet2}
          placeholder="Unit, level, etc."
        />
        <Field
          label="Suburb / City"
          value={suburb}
          onChange={setSuburb}
          error={showErrors ? errors.suburb : ""}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field
            label="State"
            value={state}
            onChange={setState}
            placeholder="e.g. QLD"
            error={showErrors ? errors.state : ""}
          />
          <Field
            label="Postcode"
            value={postcode}
            onChange={setPostcode}
            placeholder="e.g. 4064"
            error={showErrors ? errors.postcode : ""}
          />
        </div>

        <div className="flex gap-[10px] justify-end mt-2">
          <button
            type="button"
            onClick={onClose}
            className="bg-[#eee] border-none rounded-[7px] py-[9px] px-5 text-sm cursor-pointer"
            style={{ fontFamily: "inherit" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="bg-zephyr-teal text-white border-none rounded-[7px] py-[9px] px-5 text-sm font-bold cursor-pointer"
            style={{ fontFamily: "inherit" }}
          >
            Save address
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
